/**
 * widgets.ts — reusable Pixi UI primitives built in the Calm Premium style.
 *
 * Cards, pills, gradient fills, soft-shadow panels, text helpers and a Button
 * with press squash. Everything pulls colour/radius/type from theme.ts.
 */
import {
  Container,
  Graphics,
  Text,
  TextStyle,
  type FillGradient,
  type TextStyleFontWeight,
} from 'pixi.js';
import { FillGradient as Grad } from 'pixi.js';
import theme, { hex } from '../render/theme';
import { Tweens } from '../util/tween';
import { easeOutCubic, popSettle } from '../util/ease';

export type Align = 'left' | 'center' | 'right';

export function makeText(
  value: string,
  opts: {
    size?: number;
    weight?: TextStyleFontWeight;
    color?: number;
    align?: Align;
    letterSpacing?: number;
    wordWrapWidth?: number;
  } = {},
): Text {
  const style = new TextStyle({
    fontFamily: theme.font.familyName + ', Nunito, sans-serif',
    fontSize: opts.size ?? 16,
    fontWeight: (opts.weight ?? '700') as TextStyleFontWeight,
    fill: opts.color ?? theme.color.ink,
    align: opts.align ?? 'left',
    letterSpacing: opts.letterSpacing ?? 0,
    wordWrap: opts.wordWrapWidth != null,
    wordWrapWidth: opts.wordWrapWidth ?? 0,
  });
  const t = new Text({ text: value, style });
  t.resolution = Math.min(3, Math.ceil(window.devicePixelRatio || 1) + 1);
  return t;
}

/** Vertical gradient fill across a height. */
export function vGradient(stops: readonly string[], height: number, width = 0): FillGradient {
  const g = new Grad({
    type: 'linear',
    start: { x: 0, y: 0 },
    end: { x: 0, y: 1 },
    textureSpace: 'local',
  });
  void width;
  void height;
  stops.forEach((c, i) => g.addColorStop(i / (stops.length - 1), hex(c)));
  return g;
}

export function hGradient(stops: readonly string[]): FillGradient {
  const g = new Grad({
    type: 'linear',
    start: { x: 0, y: 0 },
    end: { x: 1, y: 0 },
    textureSpace: 'local',
  });
  stops.forEach((c, i) => g.addColorStop(i / (stops.length - 1), hex(c)));
  return g;
}

/** A rounded card with a soft shadow. Returns the container (shadow+fill). */
export function card(
  w: number,
  h: number,
  opts: { radius?: number; fill?: number | FillGradient; shadow?: boolean; stroke?: number } = {},
): Container {
  const c = new Container();
  const r = opts.radius ?? theme.radius.card;
  if (opts.shadow !== false) {
    const sh = new Graphics();
    const s = theme.shadow.soft;
    sh.roundRect(-2, s.offsetY, w + 4, h, r)
      .fill({ color: s.color, alpha: s.alpha });
    // crude soft shadow: a few stacked blurred-ish layers
    const sh2 = new Graphics();
    sh2.roundRect(2, s.offsetY + 6, w - 4, h, r).fill({ color: s.color, alpha: s.alpha * 0.5 });
    c.addChild(sh2, sh);
  }
  const g = new Graphics();
  g.roundRect(0, 0, w, h, r).fill(opts.fill ?? theme.color.white);
  if (opts.stroke != null) g.roundRect(0, 0, w, h, r).stroke({ width: 1.5, color: opts.stroke, alpha: 0.5 });
  c.addChild(g);
  return c;
}

/** Glass tray panel (translucent white). */
export function glassPanel(w: number, h: number, radius: number = theme.radius.card): Graphics {
  const g = new Graphics();
  g.roundRect(0, 0, w, h, radius).fill({ color: theme.color.white, alpha: theme.color.glassAlpha });
  g.roundRect(0, 0, w, h, radius).stroke({ width: 1, color: theme.color.white, alpha: theme.color.glassStrokeAlpha });
  return g;
}

/** A fully-rounded pill. */
export function pill(
  w: number,
  h: number,
  fill: number | FillGradient,
  alpha = 1,
): Graphics {
  const g = new Graphics();
  g.roundRect(0, 0, w, h, h / 2).fill({ color: 0, alpha: 0 });
  g.roundRect(0, 0, w, h, h / 2).fill(typeof fill === 'number' ? { color: fill, alpha } : fill);
  return g;
}

export interface ButtonOpts {
  width: number;
  height: number;
  label: string;
  fill?: number | FillGradient;
  textColor?: number;
  fontSize?: number;
  fontWeight?: TextStyleFontWeight;
  radius?: number;
  icon?: string; // leading glyph/emoji
  onTap?: () => void;
}

/** Interactive button with press squash + release pop. */
export class Button extends Container {
  private bg: Graphics;
  private labelText: Text;
  private opts: ButtonOpts;
  private _enabled = true;
  private body = new Container();

  constructor(opts: ButtonOpts) {
    super();
    this.opts = opts;
    this.bg = new Graphics();
    this.labelText = makeText(opts.label, {
      size: opts.fontSize ?? 18,
      weight: opts.fontWeight ?? '800',
      color: opts.textColor ?? theme.color.white,
      align: 'center',
    });
    this.body.addChild(this.bg, this.labelText);
    this.addChild(this.body);
    this.redraw();
    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.on('pointerdown', this.onDown, this);
    this.on('pointerup', this.onUp, this);
    this.on('pointerupoutside', this.onUpOutside, this);
    this.on('pointertap', () => {
      if (this._enabled) this.opts.onTap?.();
    });
  }

  setLabel(s: string): void {
    this.labelText.text = s;
    this.layoutLabel();
  }

  setEnabled(v: boolean): void {
    this._enabled = v;
    this.eventMode = v ? 'static' : 'none';
    this.alpha = v ? 1 : 0.45;
  }

  get enabled(): boolean {
    return this._enabled;
  }

  setFill(fill: number | FillGradient): void {
    this.opts.fill = fill;
    this.redraw();
  }

  resize(w: number, h: number): void {
    this.opts.width = w;
    this.opts.height = h;
    this.redraw();
  }

  private redraw(): void {
    const { width: w, height: h } = this.opts;
    const r = this.opts.radius ?? theme.radius.pill;
    this.bg.clear();
    this.bg.roundRect(0, 0, w, h, r).fill(this.opts.fill ?? theme.color.teal);
    // subtle top sheen
    this.bg.roundRect(2, 2, w - 4, h * 0.5, r).fill({ color: theme.color.white, alpha: 0.12 });
    this.layoutLabel();
    // center body pivot for squash
    this.body.pivot.set(w / 2, h / 2);
    this.body.position.set(w / 2, h / 2);
  }

  private layoutLabel(): void {
    const { width: w, height: h } = this.opts;
    const prefix = this.opts.icon ? this.opts.icon + '  ' : '';
    if (this.labelText.text.indexOf(prefix) !== 0 && prefix)
      this.labelText.text = prefix + this.labelText.text.replace(prefix, '');
    this.labelText.anchor.set(0.5);
    this.labelText.position.set(w / 2, h / 2 + 1);
  }

  private onDown(): void {
    if (!this._enabled) return;
    Tweens.to({
      from: this.body.scale.x,
      to: 0.94,
      duration: theme.motion.fast,
      ease: easeOutCubic,
      onUpdate: (v) => { if (!this.body.destroyed) this.body.scale.set(v); },
    });
  }
  private onUp(): void {
    this.bounce();
  }
  private onUpOutside(): void {
    this.bounce();
  }
  private bounce(): void {
    Tweens.to({
      from: this.body.scale.x,
      to: 1,
      duration: theme.motion.base,
      ease: popSettle,
      onUpdate: (v) => { if (!this.body.destroyed) this.body.scale.set(v); },
    });
  }
}

/** Pop a container in with a back-ease scale. */
export function popIn(node: Container, delay = 0, from = 0.6): void {
  node.scale.set(from);
  node.alpha = 0;
  Tweens.to({
    from: 0,
    to: 1,
    duration: theme.motion.base,
    delay,
    ease: popSettle,
    onUpdate: (v) => {
      if (node.destroyed) return;
      node.scale.set(from + (1 - from) * v);
      node.alpha = Math.min(1, v * 1.4);
    },
  });
}

/** Draw a progress ring (arc) into a Graphics. */
export function drawRing(
  g: Graphics,
  cx: number,
  cy: number,
  radius: number,
  thickness: number,
  progress: number,
  color: number,
  trackColor: number,
  trackAlpha = 0.25,
): void {
  g.clear();
  g.circle(cx, cy, radius).stroke({ width: thickness, color: trackColor, alpha: trackAlpha });
  if (progress > 0) {
    const start = -Math.PI / 2;
    const end = start + Math.PI * 2 * Math.min(1, progress);
    g.arc(cx, cy, radius, start, end).stroke({
      width: thickness,
      color,
      cap: 'round',
    });
  }
}
